/**
 * Wellness data adapter — shared by:
 *   • Today grid  → adaptiveWidgets.jsx <PhysicalWellnessWidget>
 *   • Metrics    → pages/PatientProgress.jsx wellness_trend
 *
 * Rules (per product spec — DO NOT change without approval):
 *  1. Merge `/api/checkins/mine` (daily) + `/api/coach/checkins/mine` (coach)
 *     into one row per ISO date. Never emit duplicate rows for the same date.
 *  2. Field-level precedence uses SOURCE (source="coach_mirror" on daily
 *     means the daily row was auto-derived by the coach submission; source
 *     ="daily_mirror" on coach means the coach row was auto-derived by the
 *     daily submission). Auto-derived values are treated as ABSENT for the
 *     fields the mirror had to synthesise so the chart never shows a
 *     fabricated number.
 *  3. `fatigue` — DIRECT ONLY. Take daily.fatigue when the daily row was NOT
 *     itself created by a coach mirror. Never derive from `10 - energy*2`.
 *     If unavailable → null (chart shows a gap).
 *  4. `sleep_quality` (1–5) — read from coach.sleep_quality only when the
 *     coach row was NOT itself a daily mirror (mirror derives sleep_quality
 *     from the 0–10 sleep-hours slider so it isn't a directly-recorded
 *     patient rating).
 *  5. `pain`, `wellness`, `confidence`, `readiness`, `stress` — daily rows
 *     store them directly on the 0-10 scale used by the charts. Coach rows
 *     store 1-5 scales for stress/confidence/energy which we don't rewrite
 *     into a fake 0-10 value. Precedence: daily direct wins.
 *  6. Merging never overwrites a valid number with a newer missing value —
 *     if the newer submission omits a field, the older direct value stays.
 *  7. Timezone: rows are grouped by their stored `date` (YYYY-MM-DD, London
 *     day-key set server-side). No client-side timezone conversion so both
 *     surfaces render the same dates.
 */
import api from "@/lib/api";

/** Extract the day-key for grouping. Prefer the stored `date` string; fall
 *  back to created_at (ISO) if the row predates the day-key field. */
function dayKey(row) {
  if (row?.date && /^\d{4}-\d{2}-\d{2}/.test(row.date)) return row.date.slice(0, 10);
  return (row?.created_at || "").slice(0, 10);
}

function isDailyMirror(row)  { return row?.source === "coach_mirror"; }
function isCoachMirror(row)  { return row?.source === "daily_mirror"; }

/** Prefer the first non-nullish value in the argument list.
 *  Explicitly distinguishes `0` (valid) from `null`/`undefined`. */
function first(...vals) {
  for (const v of vals) if (v !== null && v !== undefined) return v;
  return null;
}

/**
 * Merge two arrays of check-in rows into a chronologically sorted array of
 * `{date, pain, fatigue, sleep_quality, sleep_hours, stress, wellness,
 *   confidence, readiness}` records. One row per date.
 */
export function mergeCheckins(dailyList = [], coachList = []) {
  const byDate = new Map();
  const upsert = (key) => {
    if (!byDate.has(key)) byDate.set(key, { date: key, _dailies: [], _coaches: [] });
    return byDate.get(key);
  };
  for (const d of dailyList || []) {
    const k = dayKey(d); if (!k) continue;
    upsert(k)._dailies.push(d);
  }
  for (const c of coachList || []) {
    const k = dayKey(c); if (!k) continue;
    upsert(k)._coaches.push(c);
  }

  const out = [];
  for (const bucket of byDate.values()) {
    // If multiple submissions on same day, prefer the latest by created_at.
    const daily = [...bucket._dailies].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0] || null;
    const coach = [...bucket._coaches].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0] || null;

    const dailyDirect = daily && !isDailyMirror(daily) ? daily : null;
    const coachDirect = coach && !isCoachMirror(coach) ? coach : null;

    // Fatigue: daily-direct only. Never derived. Never coerced.
    const fatigue = dailyDirect?.fatigue ?? null;

    // Sleep quality on 1-5. Direct-only from coach.
    const sq = coachDirect?.sleep_quality;
    const sleep_quality = typeof sq === "number" && sq >= 1 && sq <= 5 ? sq : null;

    // Sleep hours — direct-only from coach (daily's `sleep` is 0-10 slider,
    // not hours). Left as null when only a daily mirror is present.
    const sh = coachDirect?.sleep_hours;
    const sleep_hours = typeof sh === "number" && sh >= 0 ? sh : null;

    out.push({
      date: bucket.date,
      pain:       first(dailyDirect?.pain,       daily?.pain,       coachDirect?.pain,       coach?.pain),
      fatigue,
      sleep_quality,
      sleep_hours,
      // 0-10 metrics — direct from daily only. We do NOT rewrite coach 1-5
      // stress/readiness into a fake 0-10 value.
      stress:     dailyDirect?.stress     ?? daily?.stress     ?? null,
      wellness:   dailyDirect?.wellness   ?? daily?.wellness   ?? null,
      confidence: dailyDirect?.confidence ?? daily?.confidence ?? dailyDirect?.readiness ?? daily?.readiness ?? null,
      readiness:  dailyDirect?.readiness  ?? daily?.readiness  ?? null,
    });
  }
  out.sort((a, b) => a.date.localeCompare(b.date));
  return out;
}

/** Fetch + merge in one call. Consumers should still cache with SWR. */
export async function fetchMergedWellness() {
  const [d, c] = await Promise.all([
    api.get("/checkins/mine").then((r) => r.data).catch(() => []),
    api.get("/coach/checkins/mine").then((r) => r.data).catch(() => []),
  ]);
  return mergeCheckins(
    Array.isArray(d) ? d : [],
    Array.isArray(c) ? c : [],
  );
}
